/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, August 21st 2022, 5:56:49 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import AccountTreeIcon from "@mui/icons-material/AccountTree";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import { TaskTemplate, NextActivation } from "lib/schemas";
import { getNewActivationDefault } from "lib/factories";
import { useDeleteNextActivation } from "service-builder/services/OutgoingBranchesConfigurationAPI";
import { APIOne2ManyListEditorFormInput, APIOne2ManyListEditorItem } from "ui-widgets/forms";
import { APISERVICEPATHS } from "lib/api";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { OSActionButton, Typography, useToggleState } from "ui-widgets";
import { ActivationBranchForm } from "./ActivationBranchForm";

interface ActivationBranchListProps {
  onSaveActivationBranchCallback: (savedActivation: NextActivation) => void;
  onDeleteActivationBranchCallback: (deletedActivation: NextActivation) => void;
}
export const ActivationBranchList = ({
  onSaveActivationBranchCallback,
  onDeleteActivationBranchCallback,
}: ActivationBranchListProps) => {
  const { watchedTaskTemplateId, getNewActivation } = useActivationBranchListLogic();

  const { handleClose, handleOpen, open } = useToggleState();

  return (
    <>
      <APIOne2ManyListEditorFormInput<TaskTemplate, NextActivation>
        apiServicePath={APISERVICEPATHS.nextActivations}
        foreignKey="task_definition"
        foreignKeyId={watchedTaskTemplateId}
        uniqueIdentifier="id"
        renderListView={({
          itemsListState: {
            savedItems: activationBranches,
            selectedItemId: selectedActivationBranchId,
            itemCreationQueue,
          },
          updateListAndSelectIfNewItem: updateActivationBranchList,
          createNewItem: createNewActivationBranch,
          selectItem: selectActivationBranch,
          findItem: findActivationBranch,
        }) => {
          const onClickAddActivationBranch = () => {
            createNewActivationBranch(getNewActivation());
            selectActivationBranch(null);
            handleOpen();
          };

          const onClickEditActivationBranch = (activationBranchId: NextActivation["id"]) => {
            selectActivationBranch(activationBranchId);
            handleOpen();
          };

          const getActivationBranchToEdit = (): NextActivation => {
            return selectedActivationBranchId
              ? findActivationBranch(selectedActivationBranchId).item
              : itemCreationQueue[0];
          };

          const onSaveActivationBranch = (nextActivation: NextActivation) => {
            updateActivationBranchList(nextActivation);
            onSaveActivationBranchCallback(nextActivation);
          };

          return (
            <>
              <List sx={{ width: "100%", bgcolor: "background.paper" }}>
                <Stack spacing={2}>
                  {activationBranches.map((activationBranch) => {
                    let listItemTextPrefix: string = "";
                    let listItemTextSuffix: string = "";

                    switch (activationBranch.conditions_count) {
                      case 0:
                        listItemTextPrefix = "Always activate  ";
                        break;
                      default:
                        listItemTextPrefix = "Activate ";
                        listItemTextSuffix = `when ${activationBranch.condition_logic_type} of ${
                          activationBranch.conditions_count
                        } ${activationBranch.conditions_count === 1 ? "condition is" : "conditions are"} met.`;
                    }
                    return (
                      <APIOne2ManyListEditorItem
                        key={activationBranch.id}
                        listItemId={activationBranch.id}
                        useDeleteOSItem={useDeleteNextActivation}
                        primaryText={activationBranch.description}
                        secondaryText={
                          <div>
                            <Typography
                              sx={{ display: "inline" }}
                              component="span"
                              variant="subtitle2"
                              color="text.primary"
                            >
                              {listItemTextPrefix}
                            </Typography>
                            <Typography sx={{ display: "inline" }} component="span" variant="subtitle1" color="primary">
                              {activationBranch.ontrue_task_definition_title}{" "}
                            </Typography>
                            {listItemTextSuffix}
                          </div>
                        }
                        onClickEditItem={() => onClickEditActivationBranch(activationBranch.id)}
                        onDeleteItemCallback={() => onDeleteActivationBranchCallback(activationBranch)}
                        listItemIcon={<AccountTreeIcon color="secondary" />}
                      />
                    );
                  })}
                </Stack>
                <ActivationBranchForm
                  nextActivationFetchId={null}
                  nextActivationDefaultValues={getActivationBranchToEdit()}
                  updateNextActivationFromAPICallback={onSaveActivationBranch}
                  onSuccessfulAPISave={null}
                  open={open}
                  handleClose={handleClose}
                />
              </List>
              <OSActionButton actionStyle="add" onClick={onClickAddActivationBranch}>
                Add Outgoing Branch
              </OSActionButton>
            </>
          );
        }}
      />
    </>
  );
};

const useActivationBranchListLogic = () => {
  const { watch } = useOSFormContext<TaskTemplate>();

  const [watchedTaskTemplateId, watchedTaskTemplateOrgId] = watch<(keyof TaskTemplate)[]>(["id", "artifact_org"]);

  const getNewActivation = () =>
    getNewActivationDefault({
      organizationId: watchedTaskTemplateOrgId as string,
      taskTemplateId: watchedTaskTemplateId as number,
    });

  return { watchedTaskTemplateId, getNewActivation };
};
